/**
 * Plan-state machine surfaced to the mobile client.
 *
 * The mobile app uses these states (NOT the raw `User.plan` string) to gate
 * write actions and surface upgrade prompts. The web app derives the state
 * from `User.plan`, `User.trialEndsAt`, and `User.stripeSubscriptionStatus`;
 * mobile receives the computed verdict so the gating logic stays in one
 * place.
 *
 * State meanings:
 *   - ACTIVE              — full read + write; no upgrade prompt
 *   - READ_ONLY_ACCOUNT   — cancelled / lapsed paid plan; reads OK, writes blocked
 *   - FEATURE_LOCKED      — on starter / lower tier; specific writes return PLAN_LOCKED
 *   - TRIAL_EXPIRED       — trial window ended without conversion; writes return TRIAL_EXPIRED
 */
import { z } from "zod";

export const PLAN_STATES = [
  "ACTIVE",
  "READ_ONLY_ACCOUNT",
  "FEATURE_LOCKED",
  "TRIAL_EXPIRED",
] as const;

export type PlanState = (typeof PLAN_STATES)[number];

export const PlanStateSchema = z.enum(PLAN_STATES);

/**
 * Raw `User.plan` enum mirrored from Prisma (see prisma/schema.prisma User
 * model — `plan String @default("starter")`). The Prisma column is a free
 * string, but only these tier values are used in production code paths
 * (verified against `lib/plan.ts::Plan`).
 *
 * Mobile MUST treat any unknown value as `starter` for safety.
 */
export const PLAN_TIERS = [
  "starter",
  "pro_monthly",
  "pro_annual",
  "advanced",
  "business",
  "site_control",
] as const;

export type PlanTier = (typeof PLAN_TIERS)[number];

export const PlanTierSchema = z.enum(PLAN_TIERS);
